/**
 * `data-table` validators (TDD §5.1). Weighting is all-or-nothing per table (mixed →
 * error, checked first). `probability` sums to 1; `relative` needs positive weights.
 */
import type { LevelFinding, LevelValidationCtx } from '../../types.js';
import type { DataTableLevel } from './schema.js';

export function validateDataTable(data: DataTableLevel, ctx: LevelValidationCtx): LevelFinding[] {
  const findings: LevelFinding[] = [];
  const { rows } = data;
  const weightMode = data.weightMode ?? 'relative';

  // duplicate row ids
  const seen = new Set<string>();
  for (const row of rows) {
    if (seen.has(row.id)) {
      findings.push({
        severity: 'error',
        code: 'duplicate-row-id',
        message: `duplicate row id: ${row.id}`,
        at: { rowId: row.id },
      });
    }
    seen.add(row.id);
  }

  // mixed weighting (checked FIRST) — weighting is all-or-nothing
  const weightedCount = rows.filter((r) => r.weight != null).length;
  const anyWeighted = weightedCount > 0;
  const allWeighted = weightedCount === rows.length;

  if (anyWeighted && !allWeighted) {
    findings.push({
      severity: 'error',
      code: 'mixed-weighting',
      message: 'some rows have a weight and others do not — weighting is all-or-nothing per table',
    });
  } else if (allWeighted) {
    if (weightMode === 'probability') {
      for (const row of rows) {
        const w = row.weight ?? 0;
        if (w <= 0 || w > 1) {
          findings.push({
            severity: 'error',
            code: 'weight-out-of-range',
            message: `probability weight ${w} out of (0,1]`,
            at: { rowId: row.id },
          });
        }
      }
      const sum = rows.reduce((s, r) => s + (r.weight ?? 0), 0);
      if (Math.abs(sum - 1) > 1e-6) {
        findings.push({
          severity: 'error',
          code: 'weights-do-not-sum',
          message: `probability weights sum to ${sum}, expected 1`,
        });
      }
    } else {
      for (const row of rows) {
        if ((row.weight ?? 0) <= 0) {
          findings.push({
            severity: 'error',
            code: 'weight-not-positive',
            message: `relative weight must be > 0`,
            at: { rowId: row.id },
          });
        }
      }
    }
  } else {
    findings.push({
      severity: 'info',
      code: 'unweighted-table',
      message: 'no weights — table treated as uniform/unweighted',
    });
  }

  // orphan refs (only when a palette is supplied). `warn`, matching the shared
  // ref-integrity checks (design §18) — a momentarily stale palette must not
  // block editing; the generation repair loops still treat orphan-ref as fixable.
  if (ctx.palette) {
    for (const row of rows) {
      for (const ref of row.refs ?? []) {
        if (!ctx.palette.has(ref)) {
          findings.push({
            severity: 'warn',
            code: 'orphan-ref',
            message: `ref not in Content palette: ${ref}`,
            at: { rowId: row.id },
          });
        }
      }
    }
  }

  return findings;
}
